export interface SDKTheme {
  colors: {
    brand50: string;
    brand100: string;
    brand200: string;
    brand300: string;
    brand400: string;
    brand500: string;
    brand600: string;
    brand700: string;
    brand800: string;
    brand900: string;
    brandBackground: string;
    brandText: string;
    white: string;
    accentLight: string;
    accentDark: string;
    overlayBackground: string;
    toastBackground: string;
    modalBackground: string;
    textPrimary: string;
    textSecondary: string;
    borderLight: string;
  };
  fonts: {
    sans: string;
  };
}

export const defaultTheme: SDKTheme = {
  colors: {
    brand50: "#f4f0ff",
    brand100: "#e3d8fe",
    brand200: "#c2aefd",
    brand300: "#a187fc",
    brand400: "#8b6ffb",
    brand500: "#875af9",
    brand600: "#7046d3",
    brand700: "#5a39a9",
    brand800: "#452b7f",
    brand900: "#2f1e55",
    brandBackground: "hsla(257, 93%, 66%, 0.3)",
    brandText: "#3d207a",
    white: "#fff",
    accentLight: "#FFE280",
    accentDark: "#CC9E00",
    overlayBackground: "rgba(0, 0, 0, 0.5)",
    toastBackground: "rgba(90, 57, 169, 0.7)",
    modalBackground: "#ffffff",
    textPrimary: "#ffffff",
    textSecondary: "#F4F0FF",
    borderLight: "#efeddb",
  },
  fonts: {
    sans: "Inter, sans-serif",
  },
};

// Mutable theme instance — used by auth-modal-manager (imperative DOM)
// and for injecting CSS custom properties.
export let theme: SDKTheme = { ...defaultTheme };

/**
 * CSS custom property names for each theme token.
 * Static `cssClass` templates reference these vars instead of JS values,
 * so the theme can be changed at runtime after module load.
 */
export const themeVars = {
  colors: {
    brand50: "var(--ff-brand-50)",
    brand100: "var(--ff-brand-100)",
    brand200: "var(--ff-brand-200)",
    brand300: "var(--ff-brand-300)",
    brand400: "var(--ff-brand-400)",
    brand500: "var(--ff-brand-500)",
    brand600: "var(--ff-brand-600)",
    brand700: "var(--ff-brand-700)",
    brand800: "var(--ff-brand-800)",
    brand900: "var(--ff-brand-900)",
    brandBackground: "var(--ff-brand-bg)",
    brandText: "var(--ff-brand-text)",
    white: "var(--ff-white)",
    accentLight: "var(--ff-accent-light)",
    accentDark: "var(--ff-accent-dark)",
    overlayBackground: "var(--ff-overlay-bg)",
    toastBackground: "var(--ff-toast-bg)",
    modalBackground: "var(--ff-modal-bg)",
    textPrimary: "var(--ff-text-primary)",
    textSecondary: "var(--ff-text-secondary)",
    borderLight: "var(--ff-border-light)",
  },
  fonts: {
    sans: "var(--ff-font-sans)",
  },
} as const;

/**
 * Inject CSS custom properties into the document so that static
 * cssClass templates (evaluated at module load) pick up the theme
 * at render time via var() references.
 */
function injectCSSProperties(t: SDKTheme): void {
  const id = "fanfest-sdk-theme";
  let style = document.getElementById(id) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = id;
    document.head.appendChild(style);
  }

  style.textContent = `:root {
    --ff-brand-50: ${t.colors.brand50};
    --ff-brand-100: ${t.colors.brand100};
    --ff-brand-200: ${t.colors.brand200};
    --ff-brand-300: ${t.colors.brand300};
    --ff-brand-400: ${t.colors.brand400};
    --ff-brand-500: ${t.colors.brand500};
    --ff-brand-600: ${t.colors.brand600};
    --ff-brand-700: ${t.colors.brand700};
    --ff-brand-800: ${t.colors.brand800};
    --ff-brand-900: ${t.colors.brand900};
    --ff-brand-bg: ${t.colors.brandBackground};
    --ff-brand-text: ${t.colors.brandText};
    --ff-white: ${t.colors.white};
    --ff-accent-light: ${t.colors.accentLight};
    --ff-accent-dark: ${t.colors.accentDark};
    --ff-overlay-bg: ${t.colors.overlayBackground};
    --ff-toast-bg: ${t.colors.toastBackground};
    --ff-modal-bg: ${t.colors.modalBackground};
    --ff-text-primary: ${t.colors.textPrimary};
    --ff-text-secondary: ${t.colors.textSecondary};
    --ff-border-light: ${t.colors.borderLight};
    --ff-font-sans: ${t.fonts.sans};
  }`;
}

export function setTheme(custom: SDKTheme): void {
  theme = custom;
  injectCSSProperties(custom);
}

// Inject defaults immediately so var() references resolve before init()
injectCSSProperties(defaultTheme);
